import {FC, useEffect, useState} from 'react'
import axios from 'axios'
import {API_URL} from '../../../../modules/auth/core/_requests'

export interface AIOptionValues {
  aiModel: string
  aiEffort: string
  aiPromptExtra: string
}

export const defaultAIOptionValues: AIOptionValues = {
  aiModel: 'gpt-5.6-sol',
  aiEffort: 'default',
  aiPromptExtra: '',
}

interface OptionItem {
  id: string
  label: string
}

// Fallbacks in case the options endpoint is unreachable; the backend
// validates against its own allowlist either way.
const FALLBACK_MODELS: OptionItem[] = [
  {id: 'gpt-5.6-sol', label: 'GPT-5.6 Sol (flagship, best quality)'},
  {id: 'gpt-5.6-terra', label: 'GPT-5.6 Terra (balanced cost/quality)'},
  {id: 'gpt-5.6-luna', label: 'GPT-5.6 Luna (fastest, cheapest)'},
  {id: 'gpt-5.5', label: 'GPT-5.5'},
  {id: 'gpt-5', label: 'GPT-5'},
]

const FALLBACK_EFFORTS: OptionItem[] = [
  {id: 'default', label: 'Default'},
  {id: 'low', label: 'Low (faster, cheaper)'},
  {id: 'medium', label: 'Medium'},
  {id: 'high', label: 'High (slower, more thorough)'},
]

interface AIOptionsProps {
  value: AIOptionValues
  onChange: (value: AIOptionValues) => void
  showPromptExtra?: boolean
}

const AIOptions: FC<AIOptionsProps> = ({value, onChange, showPromptExtra = true}) => {
  const [models, setModels] = useState<OptionItem[]>(FALLBACK_MODELS)
  const [efforts, setEfforts] = useState<OptionItem[]>(FALLBACK_EFFORTS)

  useEffect(() => {
    let cancelled = false
    axios
      .get(`${API_URL}/api/ai/options`)
      .then((res) => {
        if (cancelled) return
        if (Array.isArray(res.data?.models) && res.data.models.length > 0) {
          setModels(res.data.models)
        }
        if (Array.isArray(res.data?.efforts) && res.data.efforts.length > 0) {
          setEfforts(res.data.efforts)
        }
      })
      .catch(() => {
        // Keep fallback lists
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className='row mb-3'>
      <div className={showPromptExtra ? 'col-md-3' : 'col-md-6'}>
        <label className='form-label'>AI Model</label>
        <select
          className='form-select'
          value={value.aiModel}
          onChange={(e) => onChange({...value, aiModel: e.target.value})}
        >
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
      </div>
      <div className={showPromptExtra ? 'col-md-3' : 'col-md-6'}>
        <label className='form-label'>Reasoning Effort</label>
        <select
          className='form-select'
          value={value.aiEffort}
          onChange={(e) => onChange({...value, aiEffort: e.target.value})}
        >
          {efforts.map((ef) => (
            <option key={ef.id} value={ef.id}>
              {ef.label}
            </option>
          ))}
        </select>
      </div>
      {showPromptExtra && (
        <div className='col-md-6'>
          <label className='form-label'>
            Add to Prompt <span className='text-muted'>(Optional)</span>
          </label>
          <textarea
            className='form-control'
            rows={2}
            placeholder='Extra instructions for the AI, e.g. tone, things to mention or avoid...'
            value={value.aiPromptExtra}
            onChange={(e) => onChange({...value, aiPromptExtra: e.target.value})}
          />
        </div>
      )}
    </div>
  )
}

export default AIOptions
